import { toRaw } from 'vue'
import type {
  BarcodeScannedEventData,
  DomEventData,
  PrepareBetslipEvent,
  PrepareCancelBetEvent,
  IntegratorParentSSBTConfig,
  BetEventsFeBetslipIdData
} from '@nsftx/seven-client-retail-vue-integrator'
import {
  useDeviceManagementPrinterStatusStore,
  useWorkingTimeStore
} from '@nsftx/seven-client-retail-vue-dm'
import { isVoucher, logService } from '@nsftx/seven-client-retail-utils'
import { useGamesStore, useTicketsStore } from '@nsftx/seven-client-retail-vue-sgp'
import { handleNotificationRequired, useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import ticketCheckService from '@/modules/ticket-check/ticketCheckService'
import { handleScannedVoucher } from '../../voucher/voucherSSBTService'
import errorParser from '@/utils/errorParser'
import {
  handleTicketPayinSuccess,
  handleTicketPayinError
} from '@/modules/ticket-payin/ticketPayinService'
import ticketCheckerService from '@/modules/ticket-checker/ticketCheckerService'
import { resolveLinkCodeFromChildId } from './integratorSsbtService'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'
import i18n from '@/plugins/translationsPlugin'

const LOG_PREFIX = '[integratorSSBTEventsService]'

const { t } = i18n.global

const onLoaded = () => {
  logService.info(`${LOG_PREFIX} Product loaded`, {
    code: 'ST_PRODUCT_LOADED'
  })
}

const onPrepareBetslip = (event: PrepareBetslipEvent) => {
  const { isPrinterValid, printerStatus } = useDeviceManagementPrinterStatusStore()
  const { addPendingTicket } = useTicketsStore()
  const { showNotification, closeNotificationById } = useNotificationsStore()
  const { feBetslipId, bets } = event.data
  const childId = event.childId
  const linkCode = resolveLinkCodeFromChildId(childId)

  logService.debug(`${LOG_PREFIX} onPrepareBetslip callback called.`, {
    code: 'ST_PREPARE_BETSLIP_EVENT_SUCCESS',
    feBetslipId,
    linkCode,
    details: {
      childId,
      uuid: event.uuid
    }
  })

  const isSlipPayinDisabledOnPaperLow =
    useCmsConfigStore().cmsConfig?.appSettings.isSlipPayinDisabledOnPaperLow ?? false

  if (isSlipPayinDisabledOnPaperLow && printerStatus.paperLow) {
    logService.error(`${LOG_PREFIX} Slip pay-in is disabled because printer paper is low.`, {
      code: 'ST_PREPARE_BETSLIP_PRINTER_PAPER_LOW_ERROR',
      feBetslipId,
      linkCode,
      details: {
        printerStatus: toRaw(printerStatus),
        childId
      }
    })

    closeNotificationById('slip_payin_disabled_print_paper_low')
    showNotification({
      id: 'slip_payin_disabled_print_paper_low',
      variant: 'error',
      text: [t('slip_payin_disabled_print_paper_low')]
    })

    return event.reject({
      status: 'NOK',
      code: 'ST_PREPARE_BETSLIP_PRINTER_PAPER_LOW_ERROR',
      data: {
        feBetslipId,
        bets
      }
    })
  }

  if (!isPrinterValid()) {
    logService.error(`${LOG_PREFIX} Printer status is not valid.`, {
      code: 'ST_PREPARE_BETSLIP_PRINTER_STATUS_ERROR',
      feBetslipId,
      linkCode,
      details: {
        printerStatus: toRaw(printerStatus),
        childId
      }
    })

    return event.reject({
      status: 'NOK',
      code: 'ST_PREPARE_BETSLIP_PRINTER_STATUS_ERROR',
      data: {
        feBetslipId,
        bets
      }
    })
  }

  const workingTimeStore = useWorkingTimeStore()
  const { isValid, nextValidTime } = workingTimeStore.isWorkingTimeValid()

  if (!isValid) {
    logService.error(`${LOG_PREFIX} Acceptors could not be started. Working time is over.`, {
      code: 'ST_PREPARE_BETSLIP_WORKING_TIME_ERROR',
      feBetslipId,
      linkCode,
      details: {
        childId
      }
    })

    showNotification({
      variant: 'warning',
      text: [t('working_time_over', { time: nextValidTime || t('common_unknown') })]
    })

    return event.reject({
      code: 'ST_PREPARE_BETSLIP_WORKING_TIME_ERROR',
      status: 'NOK',
      data: {
        feBetslipId,
        bets
      }
    })
  }

  const { getGameLinkByCode } = useGamesStore()
  const gameLink = linkCode ? getGameLinkByCode(linkCode) : undefined

  if (!gameLink) {
    logService.error(`${LOG_PREFIX} Game link not found`, {
      code: 'ST_PREPARE_BETSLIP_GAME_LINK_NOT_FOUND',
      feBetslipId,
      linkCode,
      details: {
        childId
      }
    })

    return event.reject({
      code: 'ST_PREPARE_BETSLIP_GAME_LINK_NOT_FOUND',
      status: 'NOK',
      data: {
        feBetslipId,
        bets
      }
    })
  }

  addPendingTicket(feBetslipId, gameLink)
  ticketCheckerService.handleTicketChecker({
    activeFeBetslipId: feBetslipId,
    onResolveSuccess: handleTicketPayinSuccess,
    onResolveFailed: handleTicketPayinError
  })

  return event.resolve({
    status: 'OK',
    code: '',
    data: {
      feBetslipId,
      bets
    }
  })
}

const onFailBetslip = (eventData: BetEventsFeBetslipIdData) => {
  const { setFailedTicket } = useTicketsStore()
  const { feBetslipId } = eventData

  logService.debug(`${LOG_PREFIX} onFailBetslip callback called.`, {
    code: 'ST_FAIL_BETSLIP_EVENT_SUCCESS',
    feBetslipId
  })

  try {
    setFailedTicket(feBetslipId)
  } catch (error) {
    logService.warn(`${LOG_PREFIX} Could not set ticket to failed.`, {
      ...errorParser.parseUpstream(error),
      feBetslipId
    })
  }
}

const onPrepareCancelBet = (event: PrepareCancelBetEvent) => {
  const workingTimeStore = useWorkingTimeStore()
  const { isValid } = workingTimeStore.isWorkingTimeValid()
  const { feBetslipId } = event.data

  logService.debug(`${LOG_PREFIX} onPrepareCancelBet callback called.`, {
    code: 'ST_PREPARE_CANCEL_BET_EVENT_SUCCESS',
    feBetslipId,
    details: {
      uuid: event.uuid,
      childId: event.childId
    }
  })

  if (!isValid) {
    logService.error(`${LOG_PREFIX} Acceptors could not be started.`, {
      code: 'ST_PREPARE_CANCEL_BET_WORKING_TIME_ERROR',
      feBetslipId,
      details: {
        id: 'acceptors-start-error-working-time',
        message: 'Acceptors could not be started. Working time is over.',
        code: 'ST_PREPARE_CANCEL_BET_WORKING_TIME_ERROR'
      }
    })

    return event.reject({
      code: 'ST_PREPARE_CANCEL_BET_WORKING_TIME_ERROR',
      status: 'NOK',
      data: {
        feBetslipId,
        bets: []
      }
    })
  }

  return event.resolve({
    code: '',
    status: 'OK',
    data: {
      feBetslipId,
      bets: []
    }
  })
}

const onDomEvent = (data: DomEventData) => {
  logService.debug(`${LOG_PREFIX} onDomEvent callback called.`, {
    code: 'ST_DOM_EVENT_SUCCESS',
    details: {
      type: data.type,
      params: data.params
    }
  })

  switch (data.type) {
    case 'click':
      document.body.click()
      break

    case 'touchstart':
      const touchStart = new TouchEvent('touchstart', {
        bubbles: true,
        cancelable: true,
        touches: [
          new Touch({
            identifier: Date.now(),
            target: document.body,
            clientX: 100,
            clientY: 100
          })
        ]
      })
      document.body.dispatchEvent(touchStart)
      break

    case 'touchend':
      const touchEnd = new TouchEvent('touchend', {
        bubbles: true,
        cancelable: true
      })
      document.body.dispatchEvent(touchEnd)
      break

    default:
      document.body.click()
  }
}

const onBarcodeScanned = (scanEventData: BarcodeScannedEventData) => {
  const code = scanEventData.data.code

  logService.debug(`${LOG_PREFIX} onBarcodeScanned callback called.`, {
    code: 'ST_BARCODE_SCAN_EVENT_SUCCESS',
    details: {
      code
    }
  })

  if (isVoucher(code)) {
    handleScannedVoucher(code)
  } else {
    ticketCheckService.handleScannedTicket(code)
  }
}

export const integratorSSBTEvents: IntegratorParentSSBTConfig['events'] = {
  onLoaded,
  onPrepareBetslip,
  onFailBetslip,
  onPrepareCancelBet,
  onDomEvent,
  onBarcodeScanned,
  onNotificationRequired: handleNotificationRequired
}
